import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, Star, Bot, Award, ChevronRight, Grid3X3, List, 
  MapPin, Shield, Sparkles, Trophy, Crown, CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ListingBenefitsSection from "@/components/ListingBenefitsSection";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function BestOfBestPage() {
  const [businesses, setBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [isLoading, setIsLoading] = useState(true);
  const [listingType, setListingType] = useState("all"); // all, businesses, pros

  useEffect(() => {
    const fetchBestOfBest = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("best_of_best", "true");
        
        if (searchQuery) {
          params.append("search", searchQuery);
        }

        if (listingType !== "all") {
          params.append("listing_type", listingType);
        }
        
        const res = await axios.get(`${API}/businesses?${params.toString()}`);
        setBusinesses(res.data);
      } catch (error) {
        console.error("Error fetching Best of Best:", error);
        setBusinesses([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchBestOfBest, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, listingType]);

  return (
    <div className="page-container min-h-screen" data-testid="best-of-best-page">
      {/* Hero Banner */}
      <section className="py-16 md:py-24 hero-pattern relative overflow-visible">
        <div className="hero-glow top-0 right-1/4 opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F59E0B]/10 via-transparent to-transparent" />
        
        {/* AI Horse Character GIF - Desktop */}
        <div className="absolute right-0 bottom-[-100px] w-[400px] h-[550px] pointer-events-none z-0 hidden lg:block">
          <img 
            src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/feo4srb9_Untitled%20design%20%2835%29.gif"
            alt="AI Horse Hero"
            className="w-full h-full object-contain opacity-90"
          />
        </div>
        
        {/* AI Horse Character GIF - Mobile */}
        <div className="absolute right-[-10px] bottom-[-80px] w-[180px] h-[260px] pointer-events-none block lg:hidden" style={{ zIndex: 0 }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/feo4srb9_Untitled%20design%20%2835%29.gif"
            alt="AI Horse Hero"
            className="w-full h-full object-contain opacity-70"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-6">
              <Trophy className="w-5 h-5 text-[#F59E0B]" />
              <span className="text-[#F59E0B] font-semibold">AI Certified Excellence</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              <span className="text-[#F59E0B]">Best of Best</span> Directory
            </h1>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto mb-6">
              Discover AI Certified businesses and professionals who have completed 
              certification in the Dashboard. These are the elite listings that 
              have earned the Best of Best distinction.
            </p>
            
            {/* Certification Info */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20">
                <Shield className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm text-[#10B981]">AI Certified</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20">
                <Bot className="w-4 h-4 text-[#7C3AED]" />
                <span className="text-sm text-[#7C3AED]">AI Business Hero™ Powered</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                <Crown className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-sm text-[#F59E0B]">Elite Status</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === ACTIVE BEST OF BEST LISTING BENEFITS === */}
      <ListingBenefitsSection
        title="What Makes a Best of Best Listing Different"
        subtitle="Every Best of Best Listing is a complete AI-powered lead engine — branded, tracked, and built to convert. Here's everything an active listing can include."
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Search */}
              <div className="glass-card p-4 rounded-xl">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search Best of Best..."
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    data-testid="best-search-input"
                  />
                </div>
              </div>

              {/* Listing Type Filter */}
              <div className="glass-card p-4 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-4 h-4 text-[#F59E0B]" />
                  <h3 className="text-white font-semibold">Listing Type</h3>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => setListingType("all")}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      listingType === "all"
                        ? "bg-[#F59E0B]/20 text-[#F59E0B]"
                        : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Grid3X3 className="w-4 h-4" />
                    All Best of Best
                  </button>
                  <button
                    onClick={() => setListingType("businesses")}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      listingType === "businesses"
                        ? "bg-[#F59E0B]/20 text-[#F59E0B]"
                        : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Trophy className="w-4 h-4" />
                    Best of Best Businesses
                  </button>
                  <button
                    onClick={() => setListingType("individuals")}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      listingType === "individuals"
                        ? "bg-[#F59E0B]/20 text-[#F59E0B]"
                        : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Crown className="w-4 h-4" />
                    Best of Best Pros
                  </button>
                </div>
              </div>

              {/* Certification Info */}
              <div className="glass-card p-4 rounded-xl border border-[#F59E0B]/30">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-[#F59E0B]" />
                  <h3 className="text-white font-semibold">How to Get Listed</h3>
                </div>
                <div className="space-y-2 text-sm text-[#94A3B8]">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Complete Dashboard certification</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Get AI Certified status</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Appear in Best of Best listings</span>
                  </div>
                </div>
                <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full mt-4 bg-[#F59E0B] hover:bg-[#F59E0B]/80">
                    Get Certified <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </a>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-[#94A3B8]">
                  <span className="text-white font-medium">{businesses.length}</span> AI Certified listings
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  className={viewMode === "grid" ? "text-white" : "text-[#94A3B8]"}
                >
                  <Grid3X3 className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewMode("list")}
                  className={viewMode === "list" ? "text-white" : "text-[#94A3B8]"}
                >
                  <List className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Business Grid/List */}
            {isLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="glass-card rounded-2xl p-6 animate-pulse">
                    <div className="w-14 h-14 rounded-xl bg-white/10 mb-4" />
                    <div className="h-5 bg-white/10 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-white/10 rounded w-1/2 mb-4" />
                    <div className="h-16 bg-white/10 rounded" />
                  </div>
                ))}
              </div>
            ) : businesses.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <div className="w-20 h-20 rounded-full bg-[#F59E0B]/20 flex items-center justify-center mx-auto mb-6">
                  <Trophy className="w-10 h-10 text-[#F59E0B]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">No AI Certified listings yet</h3>
                <p className="text-[#94A3B8] mb-6">Be the first to get AI Certified and appear here!</p>
                <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/80">
                    Get AI Certified <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </motion.div>
            ) : (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className={
                  viewMode === "grid"
                    ? "grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                    : "space-y-4"
                }
              >
                {businesses.map((business) => (
                  <motion.div key={business.id} variants={itemVariants}>
                    <Link
                      to={`/business/${business.slug}`}
                      className={`block glass-card rounded-2xl overflow-hidden card-lift border-[#F59E0B]/30 ${
                        viewMode === "list" ? "flex items-center" : ""
                      }`}
                      data-testid={`best-business-card-${business.slug}`}
                    >
                      <div className={viewMode === "list" ? "flex-1 p-4 flex items-center gap-4" : "p-6"}>
                        <div className={viewMode === "list" ? "flex-shrink-0" : "flex items-start justify-between mb-4"}>
                          <img
                            src={business.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(business.name)}&background=7C3AED&color=fff&size=128`}
                            alt={business.name}
                            className={`rounded-xl object-cover bg-white/5 ${
                              viewMode === "list" ? "w-12 h-12" : "w-14 h-14"
                            }`}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(business.name)}&background=7C3AED&color=fff&size=128`;
                            }}
                          />
                          {viewMode === "grid" && (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-bold border border-[#F59E0B]/30">
                              <Trophy className="w-3 h-3" /> Best of Best
                            </span>
                          )}
                        </div>
                        <div className={viewMode === "list" ? "flex-1 min-w-0" : ""}>
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-semibold font-['Outfit'] text-lg truncate">
                              {business.name}
                            </h3>
                            {viewMode === "list" && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs">
                                <Trophy className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                          <p className="text-[#7C3AED] text-sm mb-2">{business.category_name}</p>
                          <p className="text-[#94A3B8] text-sm line-clamp-2">
                            {business.short_description}
                          </p>
                          <div className="flex items-center gap-3 mt-3">
                            <div className="flex items-center gap-1 text-[#F59E0B]">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="w-3 h-3 fill-current" />
                              ))}
                              <span className="text-[#94A3B8] text-xs ml-1">
                                ({business.review_count})
                              </span>
                            </div>
                            {business.city && (
                              <span className="text-[#94A3B8] text-xs flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {business.city}
                              </span>
                            )}
                          </div>
                          {/* AI Certified Badge */}
                          <div className="flex items-center gap-2 mt-3">
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs">
                              <Shield className="w-3 h-3" /> AI Certified
                            </span>
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#7C3AED]/20 text-[#7C3AED] text-xs">
                              <Bot className="w-3 h-3" /> Active
                            </span>
                          </div>
                        </div>
                        {viewMode === "list" && (
                          <ChevronRight className="w-5 h-5 text-[#94A3B8] flex-shrink-0" />
                        )}
                      </div>
                      {viewMode === "grid" && (
                        <div className="px-6 py-4 bg-gradient-to-r from-[#F59E0B]/10 to-transparent border-t border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img
                              src={business.ai_agent_avatar}
                              alt={business.ai_agent_name}
                              className="w-6 h-6 rounded-full"
                            />
                            <span className="text-[#94A3B8] text-sm">
                              {business.ai_agent_name} • AI Hero™
                            </span>
                          </div>
                          <Bot className="w-4 h-4 text-[#00E5FF]" />
                        </div>
                      )}
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </main>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-t from-[#F59E0B]/10 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 rounded-2xl border border-[#F59E0B]/30"
          >
            <Trophy className="w-16 h-16 text-[#F59E0B] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-4">
              Want to Be in the Best of Best?
            </h2>
            <p className="text-[#94A3B8] mb-8 max-w-lg mx-auto">
              Complete your AI Certification in the Dashboard and join the elite 
              businesses and professionals featured here.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-lg px-8 py-6">
                  Get AI Certified <Award className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="btn-secondary text-lg px-8 py-6">
                  Get $120K Funding
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
